import React, { useEffect, useRef, useState} from 'react';
import { WalletCards, ClipboardPlus, SquarePen, X, Save, Loader, Plus } from 'lucide-react';
import { can_visit, notify, numberWithCommas } from "../../../helpers";
import { chronicCare, updateCareManagement } from '../../../services/audits';
import { Config } from '../../../Config';
import { Drawer } from 'vaul';
import Card from '../../Card';
import Grid from '../../Grid';
import Spacing from '../../Spacing';

export default function ChronicCareManagementModal({
  account_id,
  location_id,
  activeModal = false,
  setActiveModal = () => {}
}) {

  const [loadData, setLoadData] = useState(true);
  const [chronicCareData, setChronicCareData] = useState([]);
  const [editCareManagement, setEditCareManagement] = useState(false);
  const [saveCareManagementLoader, setSaveCareManagementLoader] = useState(false);
  const [formData, setFormData] = useState({
    account_id: account_id,
    location_id: location_id,
    potential_revenue: 0,
    revenue_eligibility: []
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    getChronicCareData(account_id, location_id);
  }, [account_id, location_id]);

  const getChronicCareData = async (accountId, locationId) => {
    try {
      const response = await chronicCare(accountId, locationId);

      if (response) {
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setChronicCareData(response?.data);
          setPrecisionFormData(response?.data);
          setLoadData(false);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getChronicCareData ~ error:", error);
    }
  };

  const setPrecisionFormData = (data) => {
    setFormData({
      account_id: account_id,
      location_id: location_id,
      ...data,
      revenue_eligibility: data.revenue_eligibility?.map(careManagement => ({
        ...careManagement
      }))
    });
  };

  const handleAddDataSource = () => {
    const newCareManagement = {
      data_source: "",
      patient: 0
    };

    setFormData((prevData) => ({
      ...prevData,
      revenue_eligibility: [...prevData.revenue_eligibility, newCareManagement]
    }));
  };

  const handleDeleteRecord = async (index) => {
    setFormData((prevData) => {
      const updatedCareManagement = prevData.revenue_eligibility.filter((_, i) => i !== index);
      return {
        ...prevData,
        revenue_eligibility: updatedCareManagement
      };
    });
  };

  const handleInputChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleCareManagementChange = (index, key, value) => {
    const updatedCareManagement = formData.revenue_eligibility.map((opp, i) => 
      i === index ? { ...opp, [key]: value } : opp
    );

    setFormData((prevData) => ({
      ...prevData,
      revenue_eligibility: updatedCareManagement
    }));
  };

  const calculateTotalPatients = () => {
    return formData?.revenue_eligibility?.reduce(
      (total, item) => total + (parseInt(item?.patients) || 0), 0
    );
  };

  const calculateTotalVisits = () => {
    return formData?.revenue_eligibility?.reduce(
      (total, item) => total + (parseInt(item?.visit) || 0), 0
    );
  };

  const calculateTotalRevenue = () => {
    return formData?.revenue_eligibility?.reduce(
      (total, item) => total + (parseFloat(item?.revenue) || 0), 0
    );
  };

  const calculateAdjustedTotalRevenue = () => {
    return formData?.revenue_eligibility?.reduce(
      (total, item) => total + (parseInt(item?.patients) || 0) * (parseInt(item?.visit) || 0) * (parseFloat(item?.revenue) || 0), 0
    );
  };

  const handleCancelEdit = () => {
    setPrecisionFormData(chronicCareData);
    setEditCareManagement(false);
  };

  const updateCareManagementReport = async (e) => {
    e.preventDefault();
    setSaveCareManagementLoader(true);

    try {
      const response = await updateCareManagement(formData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === 'success') {
            notify("success", response?.data?.message);
            await getChronicCareData(account_id, location_id);
            setEditCareManagement(false);
          } else  {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setSaveCareManagementLoader(false);
    }
  }

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={setActiveModal}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Patient Details</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">

              <div className="docpro_modal" style={{ backgroundColor: 'rgb(249, 249, 251)' }}>
                {loadData ? (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <>
                    <form onSubmit={updateCareManagementReport}>
                      <div className="docpro_page_heading_wrap audit_modal_heading">
                        <h2 className="docpro_page_heading docpro_fs_24">
                          <i
                            className="docpro_white_color"
                            style={{ backgroundColor: "#ec863c" }}
                          >
                            <ClipboardPlus width={18} height={18} />
                          </i>
                          Chronic Care Management
                        </h2>
                        <div className="docpro_page_heading_btns">
                          {can_visit('administrator') && (
                          !editCareManagement ?
                            <button 
                              type="button"
                              className="docpro_bordered_btn" 
                              style={{ background: '#1578d5', borderRadius: '5px', color: '#ffffff' }}
                              onClick={()=>{setEditCareManagement(true);}}
                            >
                              <SquarePen width={20} height={20} />
                              <span>Edit</span>
                            </button>
                          :
                            <div className="flex item-center docpro_grid_gap_10">
                              <button 
                                type="button"
                                className="docpro_bordered_btn" 
                                style={{ background: '#e5484d', borderRadius: '5px', color: '#ffffff' }}
                                onClick={handleCancelEdit}
                              >
                                <X width={20} height={20} />
                                <span>Cancel</span>
                              </button>
                              {editCareManagement && (
                                <button
                                  type="submit"
                                  style={{ borderRadius: '4px' }}
                                  className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                                  disabled={saveCareManagementLoader ? true : false}
                                >
                                  <Save width={18} height={18} />
                                  <span>Save</span>
                                  {saveCareManagementLoader && (
                                    <span className="docpro_loading">
                                      <Loader width={20} height={20} />
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <Grid variant="docpro_grid_gap_20 docpro_grid_col_3">
                        <div className="docpro_card docpro_card_md docpro_card_1">
                          <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
                            <WalletCards /> 
                            Potential Revenue
                          </h3>
                          <Spacing lg="5" sm="5" />
                          <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
                            {!editCareManagement ? 
                              <>${numberWithCommas(chronicCareData?.potential_revenue? numberWithCommas(chronicCareData?.potential_revenue) : 0.00)}</>
                            :
                              <span className="docpro_signout_input items-center">
                                $
                                <input
                                  type="text"
                                  className="docpro_fw_500"
                                  style={{ maxWidth: "160px" }}
                                  defaultValue={formData?.potential_revenue || 0}
                                  onChange={(e) => handleInputChange("potential_revenue", e.target.value)}
                                />
                              </span>
                            }
                          </p>
                        </div>
                      </Grid>
                      <Spacing lg="18" sm="18" />
                      <Card variant="docpro_card docpro_card_1">
                        <div className='flex items-center'>
                          <img src="/images/favicon.png" width={45} alt="Icon" className='mr-4' />
                          <ul className='docpro_mb_0 docpro_fs_16'>
                            <li>{Config?._AppName} provides the list of unique eligible patients for CCM.</li>
                            <Spacing lg="5" sm="5" />
                            <li>Using {Config?._AppName}'s Al-powered call summary, your care manager can complete CCM.</li>
                          </ul>
                        </div>
                      </Card>
                      <Spacing lg="22" sm="22" />
                      <div>
                        <h3 className='docpro_fs_20 docpro_mb_0 mb-1'>CCM Patient Eliaibility and Revenue Potential (Approx. Data)</h3>
                        <p className='docpro_fs_14 docpro_mb_0 docpro_olive_color'>Based on the provided patient data across various disease groups, there are more than {numberWithCommas(calculateTotalPatients())} patients eligible for CCM, each with two or more chronic conditions. Here's a breakdown of potential revenue from offering CCM services:</p>
                      </div>
                      <Spacing lg="18" sm="18" />

                      <div className="docpro_data_table_wrap">
                        <div className="docpro_table_responsive" style={{ minHeight: "100px", paddingTop: "6px" }}>
                          <table className="docpro_mb_0 opportunity_table" style={{ height: "100%" }}>
                            <thead>
                              <tr>
                                {editCareManagement && (
                                  <th style={{ width: '60px' }}></th>
                                )}
                                <th>Disease Group</th>
                                <th># of Patients</th>
                                <th>Visit / Year</th>
                                <th>Revenue / Visit</th>
                                <th>Total Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {!loadData ? (
                                formData?.revenue_eligibility?.length > 0 ? (
                                  <>
                                    {formData?.revenue_eligibility?.map((item, index) => (
                                      <tr key={index}>
                                        {editCareManagement && (
                                          <td style={{ padding: 0 }}>
                                            <div className='flex items-center' style={{ justifyContent: "end" }}>
                                              {(index === formData?.revenue_eligibility?.length - 1) ? (
                                                <div className="text-right">
                                                  <button 
                                                    type="button"
                                                    onClick={handleAddDataSource} 
                                                    className="docpro_icon_btn docpro_fw_600 docpro_primary_color mr-1" 
                                                    style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                  >
                                                    <Plus width={16} height={16} />
                                                    <span className="docpro_tip">Add</span>
                                                  </button>
                                                </div>
                                              ) : (
                                                <div style={{ minWidth: '35px' }}></div>
                                              )}
                                              {formData?.revenue_eligibility?.length > 1 && (
                                                <div className="text-right">
                                                  <button
                                                    type="button"
                                                    onClick={() => handleDeleteRecord(index)}
                                                    className="docpro_icon_btn docpro_red_color docpro_icon_btn_red "
                                                    style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                  >
                                                    <X width={16} height={16} />
                                                    <span className="docpro_tip">Remove</span>
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                        <td>
                                          {!editCareManagement ? 
                                            <>{item?.disease || ""}</>
                                          :
                                            <span className="docpro_signout_input">
                                              <input
                                                type="text"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "100%" }}
                                                value={formData?.revenue_eligibility[index]?.disease || ""}
                                                onChange={(e) => handleCareManagementChange(index, "disease", e.target.value)}
                                              />
                                            </span>
                                          }
                                        </td>
                                        <td>
                                          {!editCareManagement ? 
                                            <>{item?.patients ? numberWithCommas(item?.patients) : 0}</>
                                          :
                                            <span className="docpro_signout_input">
                                              <input
                                                type="number"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "70px" }}
                                                value={formData?.revenue_eligibility[index]?.patients || 0}
                                                onChange={(e) => handleCareManagementChange(index, "patients", parseFloat(e.target.value))}
                                                min="0"
                                              />
                                            </span>
                                          }
                                        </td>
                                        <td>
                                          {!editCareManagement ? 
                                            <>{item?.visit ? numberWithCommas(item?.visit) : 0}</>
                                          :
                                            <span className="docpro_signout_input">
                                              <input
                                                type="number"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "50px" }}
                                                value={formData?.revenue_eligibility[index]?.visit || 0}
                                                onChange={(e) => handleCareManagementChange(index, "visit", parseFloat(e.target.value))}
                                                min="0"
                                              />
                                            </span>
                                          }
                                        </td>
                                        <td>
                                          {!editCareManagement ? 
                                            <>${item?.revenue ? numberWithCommas(item?.revenue) : 0.00}</>
                                          :
                                            <span className="docpro_signout_input items-center">
                                              $
                                              <input
                                                type="number"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "50px" }}
                                                value={formData?.revenue_eligibility[index]?.revenue || 0}
                                                onChange={(e) => handleCareManagementChange(index, "revenue", parseFloat(e.target.value))}
                                              />
                                            </span>
                                          }
                                        </td>
                                        <td>
                                          {!editCareManagement ? 
                                            <>${numberWithCommas((item?.patients || 0) * (item?.visit || 0) * (item?.revenue || 0))}</>
                                          :
                                            <>${numberWithCommas((formData?.revenue_eligibility[index]?.patients || 0) * (formData?.revenue_eligibility[index]?.visit || 0) * (formData?.revenue_eligibility[index]?.revenue || 0))}</>
                                          }
                                        </td>
                                      </tr>
                                    ))}
                                    <tr>
                                      {editCareManagement && (
                                        <td></td>
                                      )}
                                      <td>
                                        <h4 className='docpro_fw_600 docpro_fs_16 docpro_mb_0 docpro_secondary_color mb-2'>Total</h4>
                                      </td>
                                      <td>
                                        <h4 className='docpro_fw_600 docpro_fs_16 docpro_mb_0 docpro_secondary_color mb-2'>{numberWithCommas(calculateTotalPatients())}</h4>
                                      </td>
                                      <td>
                                        <h4 className='docpro_fw_600 docpro_fs_16 docpro_mb_0 docpro_secondary_color mb-2'>{numberWithCommas(calculateTotalVisits())}</h4>
                                      </td>
                                      <td>
                                        <h4 className='docpro_fw_600 docpro_fs_16 docpro_mb_0 docpro_secondary_color mb-2'>${numberWithCommas(calculateTotalRevenue())}</h4>
                                      </td>
                                      <td>
                                        <h4 className='docpro_fw_600 docpro_fs_16 docpro_mb_0 docpro_secondary_color mb-2'>${numberWithCommas(calculateAdjustedTotalRevenue())} (Adjusted)</h4>
                                      </td>
                                    </tr>
                                  </>
                                ) : (
                                  <tr>
                                    <td className="no_data" colSpan="5">
                                      No data available
                                    </td>
                                  </tr>
                                )
                              ) : (
                                <tr>
                                  <td className="no_data" colSpan="5">
                                    Loading...
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <Spacing lg="20" sm="20" />
                      <p className='docpro_fs_14 docpro_primary_color docpro_mb_0'>We've adjusted the patient count to remove duplicates from overlapping disease groups and identified {numberWithCommas(calculateTotalPatients())} unique patients eligible for CCM services. This provides a clearer, more accurate foundation for pfonning and maximizing care opportunities.</p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}